import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight } from '@tabler/icons-react';
import { Badge } from '@core-ui/Components/ui/badge';
import { Button } from '@core-ui/Components/ui/button';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar';
import EmptyState from '../shared/EmptyState';
import { sk } from '../shared/skeletonClass';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@core-ui/Components/ui/table';
import type { RecentSession } from '../../hooks/useDashboardOverviewVM';

interface RecentSessionsTableProps {
  sessions: RecentSession[];
  isLoading: boolean;
  onViewAll: () => void;
  onSessionClick: (sessionId: string) => void;
}

const TYPE_COLORS: Record<string, string> = {
  HUBSPOT: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  CUSTOM_HUBSPOT: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  OUTREACH: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/30',
  SALESFORCE: 'bg-sky-500/10 text-sky-500 border-sky-500/30',
  CSV: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  FOURMATIV: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
};

const TYPE_LABELS: Record<string, string> = {
  HUBSPOT: 'HubSpot',
  CUSTOM_HUBSPOT: 'HubSpot',
  OUTREACH: 'Outreach',
  SALESFORCE: 'Salesforce',
  CSV: 'CSV',
  FOURMATIV: 'Fourmativ',
};

function formatSessionDate (dateValue: string | null): string {
  if (dateValue === null || dateValue === '') {
    return '-';
  }

  const date = new Date(dateValue);

  if (isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getInitials (name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function RecentSessionsTable ({
  sessions,
  isLoading,
  onViewAll,
  onSessionClick,
}: RecentSessionsTableProps): React.JSX.Element {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="w-full overflow-hidden rounded-xl border border-border bg-card">
        <div className="p-4 border-b border-border/60 flex items-center justify-between">
          <div>
            <div className={`h-5 w-28 mb-1.5 ${sk}`} />
            <div className={`h-3 w-52 ${sk}`} />
          </div>
          <div className={`h-4 w-16 ${sk}`} />
        </div>
        <div className="p-4 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-40">
                <div className={`h-7 w-7 rounded-full ${sk}`} />
                <div className={`h-4 w-24 ${sk}`} />
              </div>
              <div className={`h-4 w-32 ${sk}`} />
              <div className={`h-4 w-20 ${sk}`} />
              <div className={`h-5 w-16 rounded-full ${sk}`} />
              <div className={`h-4 w-8 ml-auto ${sk}`} />
              <div className={`h-4 w-10 ${sk}`} />
              <div className={`h-4 w-6 ${sk}`} />
              <div className={`h-4 w-20 ${sk}`} />
              <div className={`h-5 w-20 rounded-full ${sk}`} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border bg-card">
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground">{t('dashboard.overview.callSessions.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('dashboard.overview.callSessions.description')}</p>
        </div>
        <Button variant="link" size="sm" className="text-primary" onClick={onViewAll}>
          {t('dashboard.overview.callSessions.viewAll')} <IconArrowRight size={14} className="ml-1" />
        </Button>
      </div>

      {sessions.length === 0 && (
        <div className="px-4 pb-4">
          <EmptyState message={t('dashboard.overview.callSessions.empty')} />
        </div>
      )}

      {sessions.length > 0 && <div className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/60">
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('dashboard.overview.callSessions.sdr')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('dashboard.overview.callSessions.contactList')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('dashboard.overview.callSessions.organization')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('dashboard.overview.callSessions.type')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground text-right">{t('dashboard.overview.callSessions.calls')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground text-right">{t('dashboard.overview.callSessions.connectPercent')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground text-right">{t('dashboard.overview.callSessions.appts')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('dashboard.overview.callSessions.date')}</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('dashboard.overview.callSessions.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((session) => (
              <TableRow
                key={session.id}
                className="cursor-pointer border-b border-border/60 hover:bg-muted/50 transition-colors"
                onClick={() => {
                  onSessionClick(session.id);
                }}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                        {getInitials(session.sdrName ?? 'UN')}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-foreground">{session.sdrName ?? t('dashboard.overview.callSessions.unknown')}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-foreground">{session.listNames}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{session.organizationName ?? '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] font-medium ${TYPE_COLORS[session.type] ?? ''}`}>
                    {TYPE_LABELS[session.type] ?? session.type}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-foreground">{session.totalCalls}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-emerald-500 font-medium">
                  {session.connectRate}%
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums font-medium text-foreground">
                  {session.appointmentsSet}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatSessionDate(session.startDate)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-medium ${
                      session.status === 'finished'
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                        : 'bg-amber-500/10 text-amber-500 border-amber-500/30'
                    }`}
                  >
                    {session.status === 'finished'
                      ? t('dashboard.overview.callSessions.finished')
                      : t('dashboard.overview.callSessions.inProgress')}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>}
    </div>
  );
}
